import React, { useState, useEffect, useMemo } from 'react';
import { getRescheduleQueue, updateAppointment, cancelAppointment, getBookingConfig } from '../api';
import {
  DEFAULT_BOOKING_SCHEDULE,
  buildHoursByStep,
} from '../utils/bookingSchedule';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import { AlertCircle, Calendar, Clock, User, RefreshCw } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const addOneHour = (startHhMm) => {
  const [h, m] = startHhMm.split(':').map(Number);
  return `${String(h + 1).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}`;
};

export default function RescheduleQueue() {
  const [bookingSchedule, setBookingSchedule] = useState(DEFAULT_BOOKING_SCHEDULE);
  const closedHours = useMemo(
    () =>
      buildHoursByStep(
        bookingSchedule.schedule_start,
        bookingSchedule.schedule_end,
        bookingSchedule.slot_step_minutes
      ),
    [bookingSchedule]
  );

  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchQueue = async () => {
    try {
      const response = await getRescheduleQueue();
      // Laravel devuelve {data: [...]}, Axios extrae el data, entonces accedemos a .data.data
      setAppointments(Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar citas'));
    }
  };

  useEffect(() => {
    fetchQueue();
  }, []);

  useEffect(() => {
    getBookingConfig()
      .then((res) => {
        const d = res.data?.data;
        if (d?.schedule_start && d?.schedule_end) {
          setBookingSchedule({
            schedule_start: d.schedule_start,
            schedule_end: d.schedule_end,
            slot_step_minutes: Number(d.slot_step_minutes) || 15,
          });
        }
      })
      .catch(() => {});
  }, []);

  const handleOpenDrawer = (appointment) => {
    setSelectedAppointment(appointment);
    const start = (appointment.start_time || bookingSchedule.schedule_start).slice(0, 5);
    const startClosed = closedHours.includes(start) ? start : closedHours[0];
    setFormData({
      date: '',
      start_time: startClosed,
      end_time: addOneHour(startClosed),
    });
    setDrawerOpen(true);
  };

  const handleReschedule = async (e) => {
    e.preventDefault();
    if (!formData.date) {
      toast.error('Selecciona una fecha');
      return;
    }
    setIsLoading(true);
    try {
      await updateAppointment(selectedAppointment.id, {
        date: formData.date,
        start_time: formData.start_time,
        end_time: addOneHour(formData.start_time),
        status: 'PENDIENTE',
      });
      toast.success('Cita reagendada');
      setDrawerOpen(false);
      fetchQueue();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al reagendar'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelClick = (apt) => {
    setAppointmentToCancel(apt);
    setCancelReason('');
    setCancelModalOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!appointmentToCancel) return;
    const reason = cancelReason?.trim();
    if (!reason) {
      toast.error('Indica el motivo de cancelación');
      return;
    }
    setCancelLoading(true);
    try {
      await cancelAppointment(appointmentToCancel.id, reason);
      toast.success('Cita cancelada');
      setCancelModalOpen(false);
      setAppointmentToCancel(null);
      fetchQueue();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cancelar'));
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 rounded-xl bg-[#9E2A2B]/10 flex items-center justify-center">
          <AlertCircle className="w-6 h-6 text-[#9E2A2B]" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[#2B2D42] font-['Manrope']">Por Reagendar</h1>
          <p className="text-[#5D5C61] mt-1">
            {appointments.length} cita{appointments.length !== 1 ? 's' : ''} pendiente
            {appointments.length !== 1 ? 's' : ''} de reagendar
          </p>
        </div>
      </div>

      {appointments.length > 0 ? (
        <div className="grid gap-4">
          {appointments.map((apt) => (
            <Card
              key={apt.id}
              className="border-l-4 border-l-[#9E2A2B] bg-white border-slate-100"
              data-testid={`reschedule-card-${apt.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#335C67] flex items-center justify-center text-white font-semibold">
                      {apt.patient?.name?.charAt(0) || 'P'}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{apt.patient?.full_name || apt.patient?.name || 'Cliente'}</CardTitle>
                      <CardDescription>
                        Cita original: {new Date(apt.date).toLocaleDateString('es-MX', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                        })}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge className="bg-[#9E2A2B] hover:bg-[#9E2A2B]">
                    Requiere reagendar
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6 text-sm text-[#5D5C61] mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{apt.date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{apt.start_time} - {apt.end_time}</span>
                  </div>
                  <span className="text-xs font-medium text-[#335C67] capitalize">{apt.type === 'virtual' ? 'Virtual' : 'Presencial'}</span>
                </div>
                {apt.reason && (
                  <p className="text-sm text-[#5D5C61] mb-4">
                    <strong>Motivo:</strong> {apt.reason}
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
                    onClick={() => handleOpenDrawer(apt)}
                    data-testid={`reschedule-btn-${apt.id}`}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reagendar
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full border-[#9E2A2B] text-[#9E2A2B] hover:bg-[#9E2A2B]/10"
                    onClick={() => handleCancelClick(apt)}
                    data-testid={`cancel-btn-${apt.id}`}
                  >
                    Cancelar cita
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-[#588157]/10 flex items-center justify-center mx-auto mb-4">
            <RefreshCw className="w-8 h-8 text-[#588157]" />
          </div>
          <h3 className="text-lg font-medium text-[#2B2D42]">Todo en orden</h3>
          <p className="text-[#5D5C61] mt-1">No hay citas pendientes de reagendar</p>
        </div>
      )}

      {/* Reschedule Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md" data-testid="reschedule-drawer">
          <SheetHeader>
            <SheetTitle className="text-2xl font-['Manrope']">Reagendar Cita</SheetTitle>
            <SheetDescription>
              Selecciona una nueva fecha y hora para la cita
            </SheetDescription>
          </SheetHeader>

          {selectedAppointment && (
            <div className="mt-4 p-4 bg-[#F4F1EA] rounded-lg">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-[#335C67]" />
                <span className="font-medium text-[#2B2D42]">
                  {selectedAppointment.patient?.full_name || selectedAppointment.patient?.name}
                </span>
              </div>
              <p className="text-sm text-[#5D5C61] mt-2">
                Fecha original: {new Date(selectedAppointment.date).toLocaleDateString('es-MX')}
              </p>
            </div>
          )}

          <form onSubmit={handleReschedule} className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#8D99AE]" />
                Nueva fecha
              </Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                data-testid="reschedule-date-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#8D99AE]" />
                Hora (citas de 1 hora; la hora final se asigna en automático)
              </Label>
              <Select
                value={formData.start_time || closedHours[0]}
                onValueChange={(value) => setFormData({
                  ...formData,
                  start_time: value,
                  end_time: addOneHour(value),
                })}
                data-testid="reschedule-start-input"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona hora" />
                </SelectTrigger>
                <SelectContent>
                  {closedHours.map((h) => (
                    <SelectItem key={h} value={h}>
                      {h} – {addOneHour(h)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8D99AE]">
                {formData.start_time && formData.end_time ? `${formData.start_time} a ${formData.end_time} (1 hora)` : 'Selecciona la hora de inicio'}
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
              disabled={isLoading}
              data-testid="reschedule-save-btn"
            >
              {isLoading ? 'Guardando...' : 'Confirmar Reagendamiento'}
            </Button>
          </form>
        </SheetContent>
      </Sheet>

      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Cancelar cita</DialogTitle>
            <p className="text-sm text-[#5D5C61]">Indica el motivo de la cancelación (obligatorio).</p>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="reschedule-cancel-reason">Motivo de cancelación</Label>
              <Textarea
                id="reschedule-cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ej: El cliente ya no requiere la cita..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setCancelModalOpen(false); setAppointmentToCancel(null); }}>
                No cancelar
              </Button>
              <Button
                type="button"
                className="bg-[#9E2A2B] hover:bg-[#9E2A2B]/90"
                onClick={handleCancelConfirm}
                disabled={cancelLoading || !cancelReason?.trim()}
              >
                {cancelLoading ? 'Cancelando...' : 'Sí, cancelar cita'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
